import {
  METRIC_METER_PER_SECOND,
  METRIC_KILOMETER,
  METRIC_HECTOPASCALS,
  METRIC_METER,
  METRIC_FEET,
  METRIC_PERCENTAGE,
  METRIC_CELSIUS,
  METRIC_KNOTS,
} from 'src/constants';
import {
  meterToKilometerConverter,
  meterToFeetConverter,
  metersPerSecondToKnotsConverter,
} from 'src/utils/converter';

export const isValidNum = (value: undefined | null | number | string): boolean => {
  return typeof value === 'number';
};

export const formatNumber = (value: number): string => {
  return value.toString().replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1,');
};

export const displayTemperature = (value: number): string => {
  return isValidNum(value) ? `${value}°${METRIC_CELSIUS}` : '';
};

export const displayHumidity = (value: number): string => {
  return isValidNum(value) ? `${value}${METRIC_PERCENTAGE}` : '';
};

export const displayPressure = (value: number): string => {
  return isValidNum(value) ? `${formatNumber(Math.round(value))} ${METRIC_HECTOPASCALS}` : '';
};

export const displayWindSpeed = (value: number): string => {
  return isValidNum(value) ? `${value.toFixed(1)} ${METRIC_METER_PER_SECOND}` : '';
};

export const displayWindDirection = (value: number): string => {
  return isValidNum(value) ? `${value}°` : '';
};

export const displayVisibility = (value: number): string => {
  return isValidNum(value)
    ? `${formatNumber(Math.round(meterToKilometerConverter(value)))} ${METRIC_KILOMETER}`
    : '';
};

export const displayCeiling = (value: number): string => {
  return isValidNum(value) ? `${formatNumber(value)} ${METRIC_FEET}` : '';
};

export const displayCoordinate = (value: number): string => {
  return isValidNum(value) ? `${value.toFixed(5)}°` : ''; // 5 decimal places provides 1m Precision/accuracy
};

export const displayAmsl = (value: number, altitudeUnits): string => {
  const unitOfDistance =
    typeof altitudeUnits === 'undefined' || !altitudeUnits || altitudeUnits === 'feet'
      ? METRIC_FEET
      : METRIC_METER;
  const val = unitOfDistance === METRIC_FEET ? meterToFeetConverter(value).toFixed(1) : value;
  return isValidNum(value) ? `${val} ${unitOfDistance}` : '';
};

export const displayNADPSpeed = (value: number) => {
  return isValidNum(value)
    ? `${formatNumber(Math.round(metersPerSecondToKnotsConverter(value)))} ${METRIC_KNOTS}`
    : '';
};

export const displayAltitude = (value: number): string => {
  return isValidNum(value) ? `${value.toFixed(0)} ${METRIC_FEET}` : '';
};

export const displayAltitudeRange = (start: number | null, end: number | null) => {
  let altitudeString: string | null = null;

  if (start && end) {
    altitudeString = `${formatNumber(Math.round(start))} ft-${formatNumber(Math.round(end))} ft`;
  } else if (start) {
    altitudeString = `>${formatNumber(Math.round(start))} ft`;
  } else if (end) {
    altitudeString = `<${formatNumber(Math.round(end))} ft`;
  }

  return altitudeString;
};

export const displaySpeedRange = (start: number | null, end: number | null) => {
  let speedString: string | null = null;

  if (start && end) {
    speedString = `${formatNumber(
      Math.round(metersPerSecondToKnotsConverter(start))
    )} kn-${formatNumber(Math.round(metersPerSecondToKnotsConverter(end)))} kn`;
  } else if (start) {
    speedString = `>${formatNumber(Math.round(metersPerSecondToKnotsConverter(start)))} kn`;
  } else if (end) {
    speedString = `<${formatNumber(Math.round(metersPerSecondToKnotsConverter(end)))} kn`;
  }

  return speedString;
};
